import * as React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import { Box, DropdownProps, MenuButtonProps } from "@mui/joy";
import Dropdown from "@mui/joy/Dropdown";
import MenuButton from "@mui/joy/MenuButton";
import Menu from "@mui/joy/Menu";
import { useCallback, useEffect } from "react";

interface HoverDropdownProps {
    /**
     * The label (text or JSX) shown on the trigger button.
     */
    buttonLabel: React.ReactNode;

    /**
     * An optional icon or element placed before the button label.
     */
    startDecorator?: React.ReactNode;

    /**
     * The menu items or custom content you want inside the `<Menu>`.
     * Not required if `to` prop is provided.
     */
    children?: React.ReactNode;

    /**
     * Optional props that are passed down to the underlying `<MenuButton>`.
     */
    buttonProps?: MenuButtonProps<"button">;

    /**
     * Optional props for the outer `<Dropdown>`.
     */
    dropdownProps?: Partial<DropdownProps>;

    /**
     * How long (in ms) we wait after the mouse leaves before closing the dropdown.
     * Defaults to 200ms.
     */
    closeDelay?: number;

    /**
     * Optional route path. If provided, the dropdown will act as a link to this route.
     */
    to?: string;
}

export default function HoverDropdown({
    buttonLabel,
    startDecorator,
    children,
    buttonProps,
    dropdownProps,
    closeDelay = 200,
    to,
}: HoverDropdownProps) {
    const [open, setOpen] = React.useState(false);

    // We'll track our scheduled close in a ref so we can cancel if user re-enters quickly.
    const closeTimer = React.useRef<number | null>(null);

    const clearCloseTimer = () => {
        if (closeTimer.current !== null) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    const handleMouseEnter = useCallback(() => {
        clearCloseTimer();
        setOpen(true);
    }, [setOpen]);

    const handleMouseLeave = useCallback(() => {
        // Schedule closing after `closeDelay` ms
        clearCloseTimer();
        closeTimer.current = window.setTimeout(() => {
            setOpen(false);
            closeTimer.current = null;
        }, closeDelay);
    }, [setOpen, closeDelay]);

    // Close on ANY click anywhere while open
    useEffect(() => {
        if (open) {
            const handleClick = () => setOpen(false);
            document.addEventListener("click", handleClick);
            return () => {
                document.removeEventListener("click", handleClick);
            };
        }
    }, [open]);

    return (
        <Dropdown
            {...dropdownProps}
            open={open}
            onOpenChange={(_event, isOpen) => {
                setOpen(isOpen);
            }}
        >
            <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                <MenuButton
                    component={to ? ReactRouterLink : "button"}
                    to={to}
                    startDecorator={startDecorator}
                    {...buttonProps}
                >
                    {buttonLabel}
                </MenuButton>

                {!to && <Menu>{children}</Menu>}
            </Box>
        </Dropdown>
    );
}
